import { NoxnLogo } from "@/components/noxn/NoxnLogo";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import L from "leaflet";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import { Loader2, MapPin, ArrowRight, ArrowLeft } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { usePageMeta } from "@/hooks/usePageMeta";
import { PortalShell } from "@/components/account/PortalShell";
import "leaflet/dist/leaflet.css";
import "./screenmap.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CITY_COORDS = {
    Brisbane: [-27.4698, 153.0251],
    Adelaide: [-34.9285, 138.6007],
    Perth: [-31.9523, 115.8613],
    Melbourne: [-37.8136, 144.9631],
    Sydney: [-33.8688, 151.2093],
};

const money = (cents) =>
    `$${((cents || 0) / 100).toLocaleString("en-AU", { maximumFractionDigits: 0 })}`;

// Deterministic small jitter so screens without exact coords don't stack.
const jitter = (id, axis) => {
    let h = 0;
    const s = `${id}-${axis}`;
    for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) % 1000;
    return (h / 1000 - 0.5) * 0.03;
};

const coordsFor = (s) => {
    if (typeof s.lat === "number" && typeof s.lng === "number") return [s.lat, s.lng];
    const base = CITY_COORDS[s.city];
    if (!base) return null;
    return [base[0] + jitter(s.id, "lat"), base[1] + jitter(s.id, "lng")];
};

const markerIcon = (available) => L.divIcon({
    className: "",
    html: `<span class="noxn-map-marker${available ? "" : " noxn-map-marker--booked"}"></span>`,
    iconSize: [18, 18],
    iconAnchor: [9, 9],
    popupAnchor: [0, -12],
});

const AvailBadge = ({ available, testid }) => (
    <span
        data-testid={testid}
        className={`inline-flex items-center gap-1 px-2 py-0.5 text-[9px] font-mono-tech uppercase tracking-widest border ${
            available
                ? "border-emerald-400/50 text-emerald-300 bg-emerald-400/10"
                : "border-white/15 text-slate-500 bg-white/5"
        }`}
    >
        <span className={`w-1.5 h-1.5 rounded-full ${available ? "bg-emerald-400 animate-pulse" : "bg-slate-500"}`} />
        {available ? "Available this week" : "Booked this week"}
    </span>
);

export default function ScreenMap() {
    usePageMeta({
        title: "Screen map · NOXN",
        description: "Browse every NOXN digital billboard across Brisbane, Sydney, Melbourne, Perth and Adelaide on a live map.",
    });
    const { user } = useAuth();
    const isClient = user && !user.role !== "client";
    const bookHref = user ? "/account/book" : "/signup";
    const bookHrefFor = (s) => (user ? `/account/book?screen=${s.id}` : "/signup");
    const backHref = isClient ? "/account" : "/";
    const [screens, setScreens] = useState(null);
    const [err, setErr] = useState(null);
    const [cityFilter, setCityFilter] = useState(null);
    const mapRef = useRef(null);
    const markerRefs = useRef({});

    useEffect(() => {
        (async () => {
            try {
                const { data } = await axios.get(`${API}/screens/public/map`);
                setScreens(data);
            } catch (e) {
                setErr(e?.response?.data?.detail || "Could not load screens.");
            }
        })();
    }, []);

    const cities = useMemo(
        () => [...new Set((screens || []).map((s) => s.city))].sort(),
        [screens]
    );
    const visible = useMemo(
        () => (screens || []).filter((s) => (!cityFilter || s.city === cityFilter) && coordsFor(s)),
        [screens, cityFilter]
    );

    const focusScreen = (s) => {
        const c = coordsFor(s);
        if (!c || !mapRef.current) return;
        mapRef.current.flyTo(c, 13, { duration: 0.8 });
        setTimeout(() => markerRefs.current[s.id]?.openPopup(), 850);
    };

    const pickCity = (city) => {
        setCityFilter(city);
        if (!mapRef.current) return;
        if (city && CITY_COORDS[city]) mapRef.current.flyTo(CITY_COORDS[city], 11, { duration: 0.8 });
        else mapRef.current.flyTo([-28.8, 134.5], 4, { duration: 0.8 });
    };

    return (
        <PortalShell title="Screen map" mainClassName="p-0">
        <div className={`${user ? "h-[calc(100vh-4rem)]" : "min-h-screen"} bg-[#0F1E3B] text-white flex flex-col`} data-testid="screen-map-page">
            {!user && (
                <header className="border-b border-white/10 px-6 lg:px-10 h-16 flex items-center justify-between shrink-0 relative z-[500]">
                    <Link to="/" data-testid="screen-map-logo" className="flex items-center">
                        <NoxnLogo variant="white" className="h-6 w-auto" />
                    </Link>
                    <div className="hidden sm:block font-mono-tech text-[10px] uppercase tracking-[0.35em] text-[#FF5A00]">/ Screen map</div>
                    <Link to={backHref} data-testid="screen-map-back" className="inline-flex items-center gap-2 text-xs font-mono-tech uppercase tracking-widest text-slate-400 hover:text-white transition-colors">
                        <ArrowLeft size={12} /> {isClient ? "My account" : "Home"}
                    </Link>
                </header>
            )}

            <div className="flex-1 flex flex-col lg:flex-row min-h-0">
                {/* Sidebar */}
                <aside className="lg:w-[380px] shrink-0 border-b lg:border-b-0 lg:border-r border-white/10 flex flex-col max-h-[45vh] lg:max-h-none">
                    <div className="p-5 border-b border-white/10">
                        <h1 className="font-display font-black tracking-tighter text-2xl">Our screens.</h1>
                        <p className="mt-1 text-xs text-slate-400">
                            {screens ? `${visible.length} live placement${visible.length === 1 ? "" : "s"}` : "Loading inventory…"}
                        </p>
                        <div className="mt-4 flex flex-wrap gap-2" data-testid="screen-map-city-filters">
                            <button
                                onClick={() => pickCity(null)}
                                data-testid="screen-map-city-all"
                                className={`px-3 py-1.5 text-[10px] font-mono-tech uppercase tracking-widest border transition-colors ${!cityFilter ? "border-[#FF5A00] bg-[#FF5A00]/10 text-[#FF5A00]" : "border-white/15 text-slate-400 hover:text-white"}`}
                            >All</button>
                            {cities.map((c) => (
                                <button
                                    key={c}
                                    onClick={() => pickCity(c)}
                                    data-testid={`screen-map-city-${c}`}
                                    className={`px-3 py-1.5 text-[10px] font-mono-tech uppercase tracking-widest border transition-colors ${cityFilter === c ? "border-[#FF5A00] bg-[#FF5A00]/10 text-[#FF5A00]" : "border-white/15 text-slate-400 hover:text-white"}`}
                                >{c}</button>
                            ))}
                        </div>
                    </div>
                    <div className="flex-1 overflow-y-auto" data-testid="screen-map-list">
                        {!screens && !err && (
                            <div className="p-5 flex items-center gap-2 text-xs text-slate-400" data-testid="screen-map-loading">
                                <Loader2 size={12} className="animate-spin" /> Loading screens…
                            </div>
                        )}
                        {err && <div className="p-5 text-xs text-[#FF5A00]" data-testid="screen-map-error">{err}</div>}
                        {screens && visible.length === 0 && (
                            <div className="p-5 text-xs text-slate-400" data-testid="screen-map-empty">No screens in this city yet.</div>
                        )}
                        {visible.map((s) => (
                            <div
                                key={s.id}
                                role="button"
                                tabIndex={0}
                                onClick={() => focusScreen(s)}
                                onKeyDown={(e) => { if (e.key === "Enter") focusScreen(s); }}
                                data-testid={`screen-map-card-${s.id}`}
                                className="w-full text-left p-4 border-b border-white/5 hover:bg-white/5 transition-colors flex gap-3 items-center group cursor-pointer"
                            >
                                {s.photo_url ? (
                                    <img src={s.photo_url} alt="" className="w-14 h-14 object-cover border border-white/10 shrink-0" />
                                ) : (
                                    <div className="w-14 h-14 border border-white/10 bg-[#1B2E55] flex items-center justify-center shrink-0">
                                        <MapPin size={16} className="text-slate-500" />
                                    </div>
                                )}
                                <div className="min-w-0 flex-1">
                                    <div className="text-sm font-semibold text-white truncate group-hover:text-[#FF5A00] transition-colors">{s.name}</div>
                                    <div className="text-[10px] font-mono-tech uppercase tracking-widest text-slate-500 truncate mt-0.5">
                                        {s.city} · {s.location}
                                    </div>
                                    <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                                        <span className="text-xs text-[#FF5A00]">{money(s.daily_rate_cents)}/day</span>
                                        <AvailBadge available={!!s.available_this_week} testid={`screen-map-avail-${s.id}`} />
                                    </div>
                                </div>
                                <Link
                                    to={bookHrefFor(s)}
                                    onClick={(e) => e.stopPropagation()}
                                    data-testid={`screen-map-book-${s.id}`}
                                    className="shrink-0 inline-flex items-center gap-1 bg-[#FF5A00] hover:bg-[#E65200] text-white px-3 py-1.5 text-[10px] font-semibold uppercase tracking-widest transition-colors"
                                >
                                    Book <ArrowRight size={10} />
                                </Link>
                            </div>
                        ))}
                    </div>
                    <div className="p-4 border-t border-white/10">
                        <Link
                            to={bookHref}
                            data-testid="screen-map-cta"
                            className="w-full inline-flex items-center justify-center gap-2 bg-[#FF5A00] hover:bg-[#E65200] text-white px-5 py-3 text-xs font-semibold uppercase tracking-widest transition-colors"
                        >
                            Start a campaign <ArrowRight size={12} />
                        </Link>
                    </div>
                </aside>

                {/* Map */}
                <div className="flex-1 min-h-[55vh] lg:min-h-0 relative" data-testid="screen-map-canvas">
                    <MapContainer
                        ref={mapRef}
                        center={[-28.8, 134.5]}
                        zoom={4}
                        scrollWheelZoom
                        className="absolute inset-0 z-0"
                    >
                        <TileLayer
                            url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
                            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                            className="noxn-map-tiles-dark"
                            maxZoom={19}
                        />
                        {visible.map((s) => {
                            const c = coordsFor(s);
                            return (
                                <Marker
                                    key={s.id}
                                    position={c}
                                    icon={markerIcon(!!s.available_this_week)}
                                    ref={(r) => { if (r) markerRefs.current[s.id] = r; }}
                                >
                                    <Popup>
                                        <div className="noxn-popup" data-testid={`screen-map-popup-${s.id}`}>
                                            {s.photo_url && <img src={s.photo_url} alt={s.name} className="noxn-popup-photo" />}
                                            <div className="noxn-popup-body">
                                                <div className="noxn-popup-name">{s.name}</div>
                                                <div className="noxn-popup-loc">{s.city} · {s.location}</div>
                                                <div className={`noxn-popup-avail ${s.available_this_week ? "is-avail" : "is-booked"}`} data-testid={`screen-map-popup-avail-${s.id}`}>
                                                    {s.available_this_week ? "● Available this week" : "● Booked this week"}
                                                </div>
                                                <div className="noxn-popup-rate">{money(s.daily_rate_cents)}/day</div>
                                                <Link to={bookHrefFor(s)} className="noxn-popup-cta" data-testid={`screen-map-popup-cta-${s.id}`}>
                                                    Book this screen →
                                                </Link>
                                            </div>
                                        </div>
                                    </Popup>
                                </Marker>
                            );
                        })}
                    </MapContainer>
                </div>
            </div>
        </div>
        </PortalShell>
    );
}
